<script setup lang="ts">
import { X } from 'lucide-vue-next'
import { RIGHT_PANEL_WIDTH } from '~/composables/useAppLayout'
import { Button } from '@/components/ui/button'

const { isRightPanelOpen, rightPanelTitle, closeRightPanel } = useAppLayout()
const layoutStore = useLayoutStore()

const PANEL_WIDTH = RIGHT_PANEL_WIDTH

// Bridge: when layoutStore opens a component, open the panel
watch(() => layoutStore.sidePanelVisible, (visible) => {
  if (visible) {
    isRightPanelOpen.value = true
  } else {
    isRightPanelOpen.value = false
  }
})

// Bridge: when panel is manually closed (e.g. X button), also close layoutStore
watch(isRightPanelOpen, (open) => {
  if (!open && layoutStore.sidePanelVisible) {
    // Just hide — the component stays alive so AI conversation / undo stack is preserved.
    // The parent page calls closeSidePanel() on unmount to fully destroy the component.
    layoutStore.hideSidePanel()
  }
})

// True when we have a dynamic component from layoutStore to render
const hasDynamicComponent = computed(() =>
  layoutStore.sidePanelVisible && !!layoutStore.sidePanelComponent
)

function handleClose() {
  layoutStore.hideSidePanel()
  closeRightPanel()
}
</script>

<template>
  <!--
    The panel container is ALWAYS in the DOM (width 0 when closed) so that
    the #design-right-panel-slot teleport target always exists for child pages.
  -->
  <div
    class="fixed top-0 right-0 bottom-0 z-[48] flex flex-col bg-card border-l border-border overflow-hidden transition-[width] duration-300 ease-in-out"
    :style="{ width: isRightPanelOpen ? `${PANEL_WIDTH}rem` : '0rem' }"
  >
    <!-- Fixed-width inner so content isn't squashed during the transition -->
    <div :style="{ width: `${PANEL_WIDTH}rem` }" class="flex flex-col h-full">

      <!-- Header — only shown when using the simple teleport slot (not a full dynamic component) -->
      <div v-if="!hasDynamicComponent" class="flex items-center gap-2.5 h-14 px-4 border-b border-border flex-shrink-0">
        <p class="text-[0.8125rem] font-medium text-foreground flex-1 leading-tight">{{ rightPanelTitle }}</p>
        <Button variant="ghost" size="auto" class="w-7 h-7 flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
          title="Luk panel"
          @click="handleClose"
        >
          <X class="w-3.5 h-3.5" />
        </Button>
      </div>

      <!-- Dynamic component from layoutStore (e.g. CanvasConfigEditor) — renders its own header/footer -->
      <!--
        v-if on component existence: keeps the instance alive (and its state) while
        the panel is just hidden/folded. v-show controls CSS visibility.
        closeSidePanel() (called on route navigation away) sets sidePanelComponent=null,
        which triggers v-if=false and properly destroys the component.
      -->
      <component
        v-if="!!layoutStore.sidePanelComponent"
        v-show="hasDynamicComponent"
        :is="layoutStore.sidePanelComponent!"
        v-bind="layoutStore.sidePanelProps"
        class="flex-1 min-h-0"
        @close="handleClose"
      />

      <!-- Content slot — pages teleport their right-panel markup here when NOT using a dynamic component -->
      <div v-show="!hasDynamicComponent" id="design-right-panel-slot" class="flex-1 overflow-y-auto" />
    </div>
  </div>
</template>
